import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Server,
  Bot,
  GitBranch,
  Activity,
  X,
  ChevronRight,
  Settings,
} from 'lucide-react';
import { cn } from '@/utils';
import { useCachedManualCheck } from '@/hooks/useApi';
import { useRegisterRefresh } from '@/contexts/RefreshContext';
import { agentsApi } from '@/services/api';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  currentPath: string;
}

interface NavItem {
  path: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number | string;
  description?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, currentPath }) => {
  const navigate = useNavigate();

  // Cached workflow data - persists across navigation
  const { data: workflowsData, refresh: refreshWorkflows } = useCachedManualCheck(
    () => agentsApi.listWorkflows({ limit: 100 }),
    'sidebar-workflows'
  );
  const activeWorkflowCount = workflowsData?.workflows?.filter(w => w.status === 'running').length || 0;

  // Register refresh handler with global system
  useRegisterRefresh('sidebar-workflows', refreshWorkflows);

  const navItems: NavItem[] = [
    {
      path: '/',
      name: 'Dashboard',
      icon: LayoutDashboard,
      description: 'System overview and key metrics',
    },
    {
      path: '/system/gkpos',
      name: 'GKPOS',
      icon: Package,
      description: 'GKPOS orders and audit trail',
    },
    {
      path: '/system/sap-ecc',
      name: 'SAP ECC',
      icon: Server,
      description: 'SAP ECC orders and audit trail',
    },
    {
      path: '/system/hybris',
      name: 'Hybris',
      icon: GitBranch,
      description: 'Hybris orders and audit trail',
    },
    {
      path: '/agents',
      name: 'AI Agents',
      icon: Bot,
      description: 'Monitor, Verify, Execute agents',
    },
    {
      path: '/learning',
      name: 'Learning Center',
      icon: Activity,
      description: 'Knowledge Bank, patterns, and feedback',
    },
    {
      path: '/ticket-analysis',
      name: 'Ticket Analysis',
      icon: Package,
      description: 'Analyze ServiceNow tickets and find playbooks',
    },
    {
      path: '/direct-test',
      name: 'Direct Agent Test',
      icon: GitBranch,
      description: 'Test direct agent calls (bypass pipeline)',
    },
    {
      path: '/workflows',
      name: 'Workflows',
      icon: Activity,
      badge: activeWorkflowCount > 0 ? activeWorkflowCount : undefined,
      description: 'Agent workflow execution history',
    },
    {
      path: '/admin',
      name: 'Admin',
      icon: Settings,
      description: 'System administration and data management',
    },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onToggle(); // Close sidebar on mobile
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">N</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">NEXUS V3</h2>
              <p className="text-xs text-gray-500">Incident Management</p>
            </div>
          </div>

          {/* Close button for mobile */}
          <button
            onClick={onToggle}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navItems.map((item) => {
            const isActive = currentPath === item.path;
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors group',
                  isActive
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <div className="flex items-center space-x-3">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                    )}
                  />
                  <div>
                    <div className="font-medium">{item.name}</div>
                    {item.description && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {item.description}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {item.badge && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {item.badge}
                    </span>
                  )}
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 transition-transform',
                      isActive ? 'text-blue-600' : 'text-gray-400'
                    )}
                  />
                </div>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-900">System Status</div>
            <div className="text-xs text-gray-500 mt-1">
              All systems operational and monitoring active incidents.
            </div>
            <div className="flex items-center mt-2 space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-xs font-medium text-green-700">Healthy</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
